import React, { useEffect, useRef, useState } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Popup } from "@progress/kendo-react-popup";
import { transformOptions } from "./Utils";
import { HighlightText } from "./HighlightText";

const SEARCH_COMBOBOX_POPUP_WIDTH = 340;

// =================== AddNewPopup ===================
export const AddNewPopup = ({ show, anchor, onClose, onConfirm, initialValue, config }) => {
  const [code, setCode] = useState(initialValue || "");
  const [name, setName] = useState("");

  useEffect(() => {
    setCode(initialValue || "");
    setName("");
  }, [initialValue, show]);

  // 手机端失焦处理
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const handleConfirm = () => {
    blurActiveElement();
    onConfirm({
      value: code,
      label: name,
      raw: {
        [config.value]: code,
        [config.labelFields?.[1] || "name"]: name,
      },
    });
  };

  const handleClose = () => {
    blurActiveElement();
    onClose();
  };

  if (!anchor) return null;

  return (
    <Popup show={show} anchor={anchor} popupClass="add-new-popup" animate offset={{ top: 8 }}>
      <div
        className="k-card k-card-body"
        style={{
          border: "none",
          padding: 16,
          width: 300,
          background: "#fff",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          borderRadius: 8,
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#333" }}>
          Add New {config?.typeLabel || "Item"}
        </h3>

        <label style={{ display: "block", marginBottom: 6, fontSize: 16, fontWeight: 400, color: "#999" }}>{config?.valueLabel || "Code"}</label>
        <input
          className="k-textbox"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{
            width: "100%",
            marginBottom: 16,
            padding: "8px 10px",
            fontSize: 16,
            borderRadius: 4,
            border: "1px solid #ccc",
            boxSizing: "border-box",
          }}
        />

        <label style={{ display: "block", marginBottom: 6, fontSize: 16, fontWeight: 400, color: "#999" }}>{config?.nameLabel || "Name"}</label>
        <input
          className="k-textbox"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: "100%",
            marginBottom: 24,
            padding: "8px 10px",
            fontSize: 16,
            borderRadius: 4,
            border: "1px solid #ccc",
            boxSizing: "border-box",
          }}
        />

        <div style={{ display: "flex", gap: 10, justifyContent: "end" }}>
          <button className="cancel-button" onClick={handleClose}>
            Cancel
          </button>
          <button className="confirm-button primary-button" disabled={!code} onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </Popup>
  );
};

// =================== AsyncComboBox ===================
export const AsyncComboBox = React.forwardRef(({
  value,
  onChange,
  fetchOptions,
  config = {},
  editable = true,
  placeholder = "Select or search",
  localAddedOptions = [], // 父组件传入的临时新增项
  onAddNew, // 父组件回调新增项
  hasDestination,
  minWidth=200,
  height=40,
  defaultOpen=false
}, ref) => {
  const [options, setOptions] = useState([]); // 后端拉取的 options
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [customSearchText, setCustomSearchText] = useState("");
  const [fetchedValues, setFetchedValues] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const [popupWidth, setPopupWidth] = useState(SEARCH_COMBOBOX_POPUP_WIDTH);
  const anchorRef = useRef();
  const popupClassNameRef = useRef(`combo-popup-${Math.random().toString(36).slice(2)}`);

  const selected = [...localAddedOptions, ...options].find((o) => o.value === value) || null;

  const comboRef = useRef(null);

  React.useImperativeHandle(ref, () => comboRef.current);

  const normalizeOptionValue = (optionValue) => {
    if (Array.isArray(optionValue)) return optionValue[0] ?? "";
    return optionValue ?? "";
  };

  useEffect(() => {
    if (defaultOpen && comboRef.current) {
        comboRef.current.focus();
    }
  }, [defaultOpen]);

  const updatePopupWidth = () => {
    setPopupWidth(SEARCH_COMBOBOX_POPUP_WIDTH);
    return SEARCH_COMBOBOX_POPUP_WIDTH;
  };

  const positionPopup = () => {
    if (typeof document === "undefined") return;

    const comboEl = anchorRef.current?.querySelector?.(".search-combobox");
    const popupEl = document.querySelector(`.${popupClassNameRef.current}`);
    const rect = comboEl?.getBoundingClientRect?.();

    if (!popupEl || !rect?.width) return;

    const width = SEARCH_COMBOBOX_POPUP_WIDTH;
    const minLeft = window.scrollX + 8;
    const maxLeft = window.scrollX + window.innerWidth - width - 8;
    const targetLeft = Math.round(rect.right + window.scrollX - width);
    const left = Math.max(minLeft, Math.min(targetLeft, maxLeft));

    popupEl.style.left = `${left}px`;
    popupEl.style.width = `${width}px`;
    popupEl.style.minWidth = `${width}px`;
    popupEl.style.maxWidth = `${width}px`;
  };

  const syncPopupPosition = () => {
    updatePopupWidth();
    if (typeof window === "undefined") return;

    window.requestAnimationFrame(positionPopup);
    setTimeout(positionPopup, 0);
    setTimeout(positionPopup, 50);
  };

  useEffect(() => {
    syncPopupPosition();

    window.addEventListener("resize", syncPopupPosition);
    return () => window.removeEventListener("resize", syncPopupPosition);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    syncPopupPosition();
    window.addEventListener("scroll", syncPopupPosition, true);
    return () => window.removeEventListener("scroll", syncPopupPosition, true);
  }, [isOpen, options.length]);

  // ---------------- 防抖输入 ----------------
  useEffect(() => {
    if (!editable) return;

    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setIsInitialized(true);
    }, 300);

    return () => clearTimeout(handler);
  }, [search, editable]);

  // ---------------- 初始化加载 value ----------------
  useEffect(() => {
    if (!editable || !fetchOptions || loading) return;

    const shouldLoadValueFirst =
      value && !fetchedValues.has(value) && !options.some(opt => opt.value === value);
    const shouldInitLoad = !shouldLoadValueFirst && options.length === 0;
    if (!shouldInitLoad && !shouldLoadValueFirst) return;
    const query = shouldLoadValueFirst ? value : "";
    const isValueLoad = shouldLoadValueFirst;


    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        if (!isMounted) return;
        const res = await fetchOptions(query);
        const raw = Array.isArray(res?.data) ? res.data : res;
        const transformed = transformOptions(raw, config);


        if (transformed.length) {
          setOptions((prev) => [...prev, ...transformed.filter((t) => !prev.some((p) => p.value === t.value))]);
          if (isValueLoad) {
            setFetchedValues(prev => new Set(prev).add(value));
          }

        }
      } finally {
        setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [value, editable, fetchOptions, fetchedValues]);

  // ---------------- 搜索加载 ----------------
  useEffect(() => {
    if (!isInitialized || !editable || !fetchOptions || debouncedSearch == null) return;

    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchOptions(debouncedSearch);
        const raw = Array.isArray(res?.data) ? res.data : res;
        const transformed = transformOptions(raw, config);

        if (!isMounted) return;

        // 搜索结果与父组件临时新增项合并
        const merged = [...transformed];
        if (debouncedSearch?.trim()) {
            const lowerSearch = debouncedSearch.trim().toLowerCase();
            localAddedOptions
              .filter((item) => 
                (item.label ?? "").toLowerCase().includes(lowerSearch) ||
                (item.value ?? "").toLowerCase().includes(lowerSearch)
              )
              .forEach((item) => {
                if (!merged.some((o) => o.value === item.value)) {
                  merged.push(item);
                }
              });
        } else {
            // 没有搜索词时直接合并全部临时新增项
            localAddedOptions.forEach((item) => {
              if (!merged.some((o) => o.value === item.value)) {
                merged.push(item);
              }
            });
        }

        setOptions(merged);
      } finally {
        setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [debouncedSearch]);

  // ---------------- 处理 localAddedOptions ----------------
  const searchTerm = (debouncedSearch ?? "").trim();
  const lowerSearch = searchTerm.toLowerCase();

  // 仅对 localAddedOptions 做过滤
  const filteredLocalAdded = !lowerSearch
    ? localAddedOptions
    : localAddedOptions.filter((item) => {
        const label = (item.label ?? "").toString().toLowerCase();
        const value = (item.value ?? "").toString().toLowerCase();
        return label.includes(lowerSearch) || value.includes(lowerSearch);
      });

  // ---------------- Add New ----------------
  
  const shouldShowAdd = !loading && ![...options, ...filteredLocalAdded].length && debouncedSearch && !hasDestination;
  const mergedOptionsMap = new Map();

  [...filteredLocalAdded, ...options].forEach(item => {
    if (!mergedOptionsMap.has(item.value)) {
      mergedOptionsMap.set(item.value, item);
    }
  });

  const enhancedOptions = shouldShowAdd
    ? [{ label: `\u2795 Add "${debouncedSearch}"`, value: "__new__", isNew: true }]
    : Array.from(mergedOptionsMap.values());

  return (
    <>
      <div ref={anchorRef}>
        <ComboBox
          ref={comboRef}
          opened={isOpen}
          onOpen={() => {
            syncPopupPosition();
            setIsOpen(true);
          }}
          onClose={() => setIsOpen(false)}
          onFocus={() => {
            syncPopupPosition();
            setIsOpen(true);
          }}
          onClick={() => {
            syncPopupPosition();
            setIsOpen(true);
          }}
          className="search-combobox"
          style={{ width: "100%", minWidth: minWidth, height: height }}
          data={enhancedOptions}
          textField="label"
          dataItemKey="value"
          value={selected}
          filterable
          suggest
          loading={loading}
          placeholder={placeholder}
          popupSettings={{
            className: `custom-combobox-popup ${popupClassNameRef.current}`,
            popupClass: "search-combobox-popup",
            width: `${popupWidth}px`,
          }}
          onFilterChange={(e) => setSearch(e.filter?.value ?? "")}
          onChange={(e) => {
            const val = e.value;
            if (val?.isNew) {
              setCustomSearchText(debouncedSearch);
              setShowAddDialog(true);
            } else {
              onChange(val?.value ?? "", val?.raw ?? null);
            }
          }}
          itemRender={(li, itemProps) => {
            const item = itemProps.dataItem || {};
            if (item?.isNew) return li;

            const raw = item.raw || {};
            const label = item.label || "";
            const [labelNumberPart, ...labelNameParts] = label.split(" ");
            const [numberField, ...nameFields] = config.labelFields || [];
            const numberPart = normalizeOptionValue(
              raw[numberField] ?? item[numberField] ?? labelNumberPart
            ).toString();
            const namePart = (
              nameFields.length
                ? nameFields
                    .map(field => normalizeOptionValue(raw[field] ?? item[field]))
                    .filter(Boolean)
                    .join(config.separator || " ")
                : labelNameParts.join(" ")
            ).toString();

            return (
              <HighlightText
                number={numberPart}
                name={namePart}
                highlight={search}
                li={li}
                enableTooltip
              />
            );
          }}
        />
      </div>

      {anchorRef.current && (
        <AddNewPopup
          show={showAddDialog}
          anchor={anchorRef.current}
          initialValue={customSearchText}
          config={config}
          onClose={() => setShowAddDialog(false)}
          onConfirm={(newItem) => {
            if (onAddNew) onAddNew(newItem); // 父组件更新临时新增项
            onChange(newItem.value, newItem.raw); // 更新 ComboBox value
            setShowAddDialog(false);
          }}
        />
      )}
    </>
  );
});
